import { useMemo } from 'react'

import { useEmployerRenewalConfig } from 'apps/employer_renewals/providers/EmployerRenewalConfigProvider'
import { useGetEmployersRenewalsRenewalConfigsIdInsurancePlansRateSummary as useGetRateSummary } from 'utils/web_api'
import { type QuotingQuoteAllowance } from 'utils/web_api.schemas'

import { useEmployer } from '../providers/EmployerProvider'
import { useQuoteResults } from '../providers/QuoteResultsProvider'
import { allowancesAreEqual, recommendedFlatAllowances } from '../utils/flatAllowanceRecommendations'

export default function useFlatAllowanceRecommendedAllowances () {
  const { renewalConfigId, employerRenewalConfig, isLoading: isLoadingRenewalConfig, isUsingFlatAllowances } = useEmployerRenewalConfig()
  const { quoteResults, isLoadingQuoteResults } = useQuoteResults()
  const { data: rateSummary, isLoading: isLoadingRateSummary } = useGetRateSummary(renewalConfigId, { query: { enabled: !!renewalConfigId } })
  const { employerIsALE } = useEmployer()

  const isLoading = isLoadingRenewalConfig || isLoadingRateSummary || isLoadingQuoteResults
  const currentYearAllowances = employerRenewalConfig?.current_year_quote.allowances ?? []
  const currentYearQuoteResults = quoteResults?.current.quote_results ?? []
  const renewalYearAllowances = employerRenewalConfig?.renewal_year_quote.allowances ?? []
  const renewalYearQuoteResults = quoteResults?.matched.quote_results ?? []

  const recommendedAllowances = useMemo((): QuotingQuoteAllowance[] => {
    if (!isUsingFlatAllowances || isLoading || !currentYearAllowances || !rateSummary?.data) {
      return []
    }

    return recommendedFlatAllowances(
      currentYearQuoteResults,
      currentYearAllowances,
      renewalYearQuoteResults,
      rateSummary.data,
      employerIsALE
    )
  }, [currentYearQuoteResults, currentYearAllowances, renewalYearQuoteResults, rateSummary?.data, isLoading, employerIsALE])

  const isUsingCurrentAllowances = useMemo(() => {
    if (!isUsingFlatAllowances || isLoading || !renewalYearAllowances) {
      return false
    }

    return allowancesAreEqual(currentYearAllowances, renewalYearAllowances)
  }, [isUsingFlatAllowances, isLoading, renewalYearAllowances, currentYearAllowances])

  const isUsingRecommendedAllowances = useMemo(() => {
    if (!isUsingFlatAllowances || isLoading || !recommendedAllowances) {
      return false
    }

    return allowancesAreEqual(renewalYearAllowances, recommendedAllowances)
  }, [isUsingFlatAllowances, isLoading, recommendedAllowances, renewalYearAllowances])

  return {
    isLoading,
    recommendedAllowances,
    isUsingCurrentAllowances,
    isUsingRecommendedAllowances,
  }
}
